import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Plus, ChevronUp, ChevronDown, Eye, EyeOff, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { EditableText } from '../EditableText';
import { EditableImage } from '../EditableImage';
import heroImageDefault from "@/assets/hero-cleaner.jpg";
import { getIconComponent, ICON_NAMES } from "@/components/admin/icon-map";
import { cn } from '@/lib/utils';
import { Services } from '@/components/home/Services';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useCompanyInfoBrief } from '@/hooks/useCompanyInfo';
import { HeroView } from '@/components/home/Hero';
import { useHeroModel } from '@/components/home/layouts/models';

// Default content structures
const DEFAULT_HERO = {
  title: 'One-off or regular cleaning',
  subtitle: "We provide reliable, quality cleaning for busy households and businesses, with clear, upfront pricing so you always know exactly what you're paying for.",
  ctaText: 'Book my cleaning',
  imageUrl: '',
  badges: [
    { icon: 'Shield', title: 'Fully insured', subtitle: 'Peace of mind' },
    { icon: 'ThumbsUp', title: '100% satisfaction', subtitle: 'Or we fix it free' },
    { icon: 'Clock', title: 'Flexible times', subtitle: '7 days a week' },
  ],
};

const DEFAULT_WHY_CHOOSE = {
  title: '',
  description: 'We are committed to making your life easier with reliable, professional cleaning',
  stats: [
    { value: '90%', label: 'Of messages replied to within the hour' },
    { value: '100%', label: 'Fully insured and vetted cleaners' },
    { value: '40+', label: 'Checklist points in every standard clean' },
    { value: '100%', label: 'Transparent pricing before we arrive' },
  ],
  features: [
    { icon: 'Shield', title: 'Fully insured', description: 'Public liability insurance for complete peace of mind.' },
    { icon: 'Clock', title: 'Flexible scheduling', description: 'Morning, afternoon, or evening. We work around your schedule.' },
    { icon: 'Heart', title: 'Same cleaner', description: 'Build a relationship with your regular cleaner who knows your home.' },
    { icon: 'Award', title: 'Quality guaranteed', description: 'Not 100% happy? We will come back and fix it for free.' },
    { icon: 'Leaf', title: 'Eco friendly options', description: 'Non-toxic, environmentally friendly products available.' },
    { icon: 'Users', title: 'Vetted professionals', description: 'All cleaners are background checked, trained and reviewed.' },
  ],
};

const DEFAULT_REVIEWS = {
  title: 'What our customers say',
  items: [
    { name: "James M.", location: "Cardiff Bay", content: "We use The Polished Version for all 5 of our AirBnB properties. The turnaround is fast, the quality is consistent, and our guests always mention how spotless the places are.", rating: 5 },
    { name: "Emma W.", location: "Newport City Centre", content: "Easy to book online and the cleaner arrived right on time. My kitchen and bathrooms have never looked better. Will definitely be booking regularly!", rating: 5 },
    { name: "Sarah T.", location: "Penarth", content: "Absolutely brilliant service! The team was professional, thorough, and left my home sparkling. Highly recommend to anyone looking for reliable cleaners.", rating: 5 },
  ],
};

const DEFAULT_CTA = {
  title: 'Ready for a spotless home?',
  subtitle: 'Pick a time that suits you and book your clean online in about a minute.',
  buttonText: 'Book my cleaning',
};

type SectionType = 'hero' | 'services' | 'whyChoose' | 'reviews' | 'cta';

interface SectionConfig {
  id: SectionType;
  name: string;
  visible: boolean;
}

const DEFAULT_SECTIONS: SectionConfig[] = [
  { id: 'hero', name: 'Hero Section', visible: true },
  { id: 'services', name: 'Services', visible: true },
  { id: 'whyChoose', name: 'Why Choose Us', visible: true },
  { id: 'reviews', name: 'Testimonials', visible: true },
  { id: 'cta', name: 'Call to Action', visible: true },
];

type HeroBadge = { icon: string; title: string; subtitle: string };
type HeroContent = typeof DEFAULT_HERO;
type WhyChooseStat = { value: string; label: string };
type WhyChooseFeature = { icon: string; title: string; description: string };
type WhyChooseContent = typeof DEFAULT_WHY_CHOOSE;
type ReviewItem = { name: string; location: string; content: string; rating: number };
type ReviewsContent = { title: string; items: ReviewItem[] };
type CtaContent = typeof DEFAULT_CTA;

interface HomepageContent {
  hero?: Partial<HeroContent>;
  whyChoose?: Partial<WhyChooseContent>;
  reviews?: Partial<ReviewsContent> | ReviewItem[];
  cta?: Partial<CtaContent>;
}

interface HomepageRendererProps {
  content: HomepageContent | null | undefined;
  onChange: (content: HomepageContent) => void;
}

// Icon Picker Component
function IconPicker({ value, onChange }: { value: string; onChange: (icon: string) => void }) {
  const [open, setOpen] = useState(false);
  const Icon = getIconComponent(value);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-primary transition-all cursor-pointer">
          <Icon className="w-6 h-6 text-primary" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="grid grid-cols-6 gap-1">
          {ICON_NAMES.map((iconName) => {
            const IconOption = getIconComponent(iconName);
            return (
              <button
                key={iconName}
                onClick={() => {
                  onChange(iconName);
                  setOpen(false);
                }}
                className={cn(
                  "p-2 rounded hover:bg-muted",
                  value === iconName && "bg-primary/10 text-primary"
                )}
                title={iconName}
              >
                <IconOption className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Rating Picker Component
function RatingPicker({ value, onChange }: { value: number; onChange: (rating: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => onChange(star)}
          className="hover:scale-110 transition-transform"
        >
          <Star className={cn("w-5 h-5", star <= value ? "text-amber-400 fill-amber-400" : "text-muted hover:text-amber-200")} />
        </button>
      ))}
    </div>
  );
}

export function HomepageRenderer({ content, onChange }: HomepageRendererProps) {
  const { org, orgId } = useAdminOrg();
  const { data: companyInfo } = useCompanyInfoBrief(orgId ?? undefined);
  const heroModel = useHeroModel();
  const [sections, setSections] = useState<SectionConfig[]>(DEFAULT_SECTIONS);
  const [carouselIndex, setCarouselIndex] = useState(0);
  
  const hero: HeroContent = { ...DEFAULT_HERO, ...content?.hero };
  const whyChoose: WhyChooseContent = { ...DEFAULT_WHY_CHOOSE, ...content?.whyChoose };
  const businessName = companyInfo?.company_name || org?.name || 'your business';
  const whyChooseTitle = !whyChoose.title || whyChoose.title === 'Why choose The Polished Version?'
    ? `Why choose ${businessName}?`
    : whyChoose.title;
  const reviewContent = content?.reviews;
  const reviewConfig = Array.isArray(reviewContent) ? undefined : reviewContent;
  const reviews: ReviewsContent = {
    title: reviewConfig?.title || DEFAULT_REVIEWS.title,
    items: Array.isArray(reviewContent) ? reviewContent : reviewConfig?.items || DEFAULT_REVIEWS.items,
  };
  const cta: CtaContent = { ...DEFAULT_CTA, ...content?.cta };

  // Auto-advance carousel
  useEffect(() => {
    if (reviews.items.length <= 3) return;
    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % Math.ceil(reviews.items.length / 3));
    }, 5000);
    return () => clearInterval(timer);
  }, [reviews.items.length]);

  const updateHero = (updates: Partial<typeof hero>) => {
    onChange({ ...content, hero: { ...hero, ...updates } });
  };

  const updateWhyChoose = (updates: Partial<typeof whyChoose>) => {
    onChange({ ...content, whyChoose: { ...whyChoose, ...updates } });
  };

  const updateReviews = (updates: Partial<typeof reviews>) => {
    onChange({ ...content, reviews: { ...reviews, ...updates } });
  };

  const updateCta = (updates: Partial<typeof cta>) => {
    onChange({ ...content, cta: { ...cta, ...updates } });
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSections(newSections);
  };

  const toggleVisibility = (id: SectionType) => {
    setSections(sections.map(s => s.id === id ? { ...s, visible: !s.visible } : s));
  };

  // Badge management
  const addBadge = () => {
    const badges = [...(hero.badges || []), { icon: 'Star', title: 'New Badge', subtitle: 'Description' }];
    updateHero({ badges });
  };

  const removeBadge = (index: number) => {
    const badges = hero.badges.filter((_, i: number) => i !== index);
    updateHero({ badges });
  };

  const updateBadge = (index: number, updates: Partial<HeroBadge>) => {
    const badges = [...hero.badges];
    badges[index] = { ...badges[index], ...updates };
    updateHero({ badges });
  };

  // Feature management
  const addFeature = () => {
    const features = [...whyChoose.features, { icon: 'Star', title: 'New Feature', description: 'Feature description here' }];
    updateWhyChoose({ features });
  };

  const removeFeature = (index: number) => {
    const features = whyChoose.features.filter((_, i: number) => i !== index);
    updateWhyChoose({ features });
  };

  // Review management
  const addReview = () => {
    const items = [...reviews.items, { name: 'New Customer', location: 'Location', content: 'Review content here...', rating: 5 }];
    updateReviews({ items });
  };

  const removeReview = (index: number) => {
    const items = reviews.items.filter((_, i: number) => i !== index);
    updateReviews({ items });
  };

  const updateReviewItem = (index: number, updates: Partial<ReviewItem>) => {
    const items = [...reviews.items];
    items[index] = { ...items[index], ...updates };
    updateReviews({ items });
  };

  const renderSectionControls = (section: SectionConfig, index: number) => (
    <div className="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-background/95 backdrop-blur rounded-lg p-1 shadow-lg border border-border">
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveSection(index, 'up')} disabled={index === 0}>
        <ChevronUp className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveSection(index, 'down')} disabled={index === sections.filter(s => s.visible).length - 1}>
        <ChevronDown className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleVisibility(section.id)}>
        <EyeOff className="h-4 w-4" />
      </Button>
    </div>
  );

  const visibleSections = sections.filter(s => s.visible);
  const visibleReviews = reviews.items.slice(carouselIndex * 3, carouselIndex * 3 + 3);
  const totalPages = Math.ceil(reviews.items.length / 3);

  return (
    <div className="min-h-full">
      {visibleSections.map((section, index) => (
        <div key={section.id} className="relative group">
          {renderSectionControls(section, index)}
          
          {section.id === 'hero' && (
            <>
              {/* The real hero of the active theme (same layout as the live site) with inline editors swapped in. */}
              <HeroView
                m={{ ...heroModel, ready: true, heroPending: false }}
                edit={{
                  title: <EditableText value={hero.title} onChange={(v) => updateHero({ title: v })} className="inline" />,
                  subtitle: (
                    <EditableText value={hero.subtitle} onChange={(v) => updateHero({ subtitle: v })} as="p" className="block" />
                  ),
                  cta: <EditableText value={hero.ctaText} onChange={(v) => updateHero({ ctaText: v })} className="inline" />,
                  image: (
                    <EditableImage
                      src={hero.imageUrl}
                      alt="Professional cleaner"
                      onChange={(url) => updateHero({ imageUrl: url })}
                      fallbackSrc={heroImageDefault}
                      className="absolute inset-0"
                      aspectRatio="auto"
                    />
                  ),
                }}
              />

              {/* Trust badges (shown in the strip under the hero on the live site) */}
              <div className="border-y border-border bg-card/60 py-4">
                <div className="container mx-auto flex flex-wrap items-center gap-3 px-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Trust badges</span>
                  {hero.badges?.map((badge: HeroBadge, i: number) => {
                    const BadgeIcon = getIconComponent(badge.icon);
                    return (
                      <div key={i} className="group/badge flex items-center gap-3 rounded-2xl bg-card p-3 shadow-sm">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary/10 transition-all hover:ring-2 hover:ring-primary">
                              <BadgeIcon className="h-5 w-5 text-primary" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-2" align="start">
                            <div className="grid grid-cols-6 gap-1">
                              {ICON_NAMES.map((iconName) => {
                                const IconOption = getIconComponent(iconName);
                                return (
                                  <button
                                    key={iconName}
                                    onClick={() => updateBadge(i, { icon: iconName })}
                                    className={cn("rounded p-2 hover:bg-muted", badge.icon === iconName && "bg-primary/10 text-primary")}
                                    title={iconName}
                                  >
                                    <IconOption className="h-4 w-4" />
                                  </button>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <div className="min-w-0">
                          <EditableText value={badge.title} onChange={(v) => updateBadge(i, { title: v })} className="block text-sm font-semibold" />
                          <EditableText value={badge.subtitle} onChange={(v) => updateBadge(i, { subtitle: v })} className="text-xs text-muted-foreground" />
                        </div>
                        <button onClick={() => removeBadge(i)} aria-label="Remove badge" className="rounded p-1 opacity-0 transition-opacity hover:bg-destructive/10 group-hover/badge:opacity-100">
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </button>
                      </div>
                    );
                  })}
                  {(hero.badges?.length || 0) < 5 && (
                    <button
                      onClick={addBadge}
                      className="flex items-center justify-center rounded-xl border-2 border-dashed border-muted-foreground/30 p-2 transition-colors hover:bg-muted"
                      aria-label="Add badge"
                    >
                      <Plus className="h-5 w-5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {section.id === 'services' && (
            <div className="pointer-events-none">
              <Services />
            </div>
          )}

          {section.id === 'whyChoose' && (
            <section className="py-16 md:py-24 bg-card">
              <div className="container mx-auto px-4">
                <div className="text-center max-w-2xl mx-auto mb-12">
                  <EditableText
                    value={whyChooseTitle}
                    onChange={(v) => updateWhyChoose({ title: v })}
                    as="h2"
                    className="text-3xl md:text-4xl font-bold text-foreground mb-4"
                  />
                  <EditableText
                    value={whyChoose.description}
                    onChange={(v) => updateWhyChoose({ description: v })}
                    as="p"
                    className="text-muted-foreground text-lg"
                  />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                  {whyChoose.stats.map((stat: WhyChooseStat, i: number) => (
                    <div key={i} className="text-center p-6 bg-background rounded-2xl border border-border">
                      <EditableText
                        value={stat.value}
                        onChange={(v) => {
                          const stats = [...whyChoose.stats];
                          stats[i] = { ...stats[i], value: v };
                          updateWhyChoose({ stats });
                        }}
                        className="text-4xl font-bold text-primary block mb-2"
                      />
                      <EditableText
                        value={stat.label}
                        onChange={(v) => {
                          const stats = [...whyChoose.stats];
                          stats[i] = { ...stats[i], label: v };
                          updateWhyChoose({ stats });
                        }}
                        className="text-muted-foreground text-sm"
                      />
                    </div>
                  ))}
                </div>
                
                {/* Editable Features Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {whyChoose.features.map((feature: WhyChooseFeature, i: number) => (
                    <div key={i} className="flex gap-4 p-6 bg-background rounded-2xl border border-border group/feature relative">
                      <IconPicker
                        value={feature.icon}
                        onChange={(icon) => {
                          const features = [...whyChoose.features];
                          features[i] = { ...features[i], icon };
                          updateWhyChoose({ features });
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <EditableText
                          value={feature.title}
                          onChange={(v) => {
                            const features = [...whyChoose.features];
                            features[i] = { ...features[i], title: v };
                            updateWhyChoose({ features });
                          }}
                          as="h3"
                          className="font-semibold text-foreground mb-1"
                        />
                        <EditableText
                          value={feature.description}
                          onChange={(v) => {
                            const features = [...whyChoose.features];
                            features[i] = { ...features[i], description: v };
                            updateWhyChoose({ features });
                          }}
                          as="p"
                          className="text-muted-foreground text-sm"
                        />
                      </div>
                      <button
                        onClick={() => removeFeature(i)}
                        className="absolute top-2 right-2 opacity-0 group-hover/feature:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add Feature Button */}
                  <button
                    onClick={addFeature}
                    className="flex gap-4 p-6 bg-background rounded-2xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 transition-colors items-center justify-center"
                  >
                    <Plus className="w-6 h-6 text-muted-foreground" />
                    <span className="text-muted-foreground font-medium">Add Feature</span>
                  </button>
                </div>
              </div>
            </section>
          )}

          {section.id === 'reviews' && (
            <section className="py-16 md:py-24 bg-background">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <EditableText
                    value={reviews.title}
                    onChange={(v) => updateReviews({ title: v })}
                    as="h2"
                    className="text-3xl md:text-4xl font-bold text-foreground mb-4"
                  />
                </div>
                
                {/* Reviews Carousel */}
                <div className="relative max-w-5xl mx-auto">
                  <div className="grid md:grid-cols-3 gap-6">
                    {visibleReviews.map((review: ReviewItem, displayIndex: number) => {
                      const actualIndex = carouselIndex * 3 + displayIndex;
                      return (
                        <div key={actualIndex} className="bg-card rounded-2xl p-6 border border-border group/review relative">
                          <div className="mb-4">
                            <RatingPicker
                              value={review.rating}
                              onChange={(rating) => updateReviewItem(actualIndex, { rating })}
                            />
                          </div>
                          <EditableText
                            value={review.content}
                            onChange={(v) => updateReviewItem(actualIndex, { content: v })}
                            as="p"
                            className="text-foreground mb-4 min-h-[80px]"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                              {review.name.charAt(0)}
                            </div>
                            <div>
                              <EditableText
                                value={review.name}
                                onChange={(v) => updateReviewItem(actualIndex, { name: v })}
                                className="font-semibold text-foreground text-sm"
                              />
                              <EditableText
                                value={review.location}
                                onChange={(v) => updateReviewItem(actualIndex, { location: v })}
                                className="text-muted-foreground text-xs"
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => removeReview(actualIndex)}
                            className="absolute top-2 right-2 opacity-0 group-hover/review:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Carousel Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 mt-8">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCarouselIndex((prev) => (prev - 1 + totalPages) % totalPages)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex gap-2">
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCarouselIndex(i)}
                            className={cn(
                              "w-2 h-2 rounded-full transition-colors",
                              i === carouselIndex ? "bg-primary" : "bg-muted-foreground/30"
                            )}
                          />
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCarouselIndex((prev) => (prev + 1) % totalPages)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Add Review Button */}
                <div className="text-center mt-8">
                  <Button variant="outline" onClick={addReview}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Review
                  </Button>
                </div>
              </div>
            </section>
          )}

          {section.id === 'cta' && (
            <section className="py-16 bg-primary">
              <div className="container mx-auto px-4 text-center">
                <EditableText
                  value={cta.title}
                  onChange={(v) => updateCta({ title: v })}
                  as="h2"
                  className="text-3xl font-bold text-primary-foreground mb-4"
                />
                <EditableText
                  value={cta.subtitle}
                  onChange={(v) => updateCta({ subtitle: v })}
                  as="p"
                  className="text-primary-foreground/80 text-lg max-w-xl mx-auto mb-8"
                />
                <Button size="xl" variant="secondary" className="bg-card text-foreground pointer-events-none">
                  <EditableText
                    value={cta.buttonText}
                    onChange={(v) => updateCta({ buttonText: v })}
                    className="inline"
                  />
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </section>
          )}
        </div>
      ))}

      {/* Hidden sections indicator */}
      {sections.filter(s => !s.visible).length > 0 && (
        <div className="p-4 bg-muted/50 border-t border-border">
          <p className="text-sm text-muted-foreground mb-2">Hidden sections:</p>
          <div className="flex flex-wrap gap-2">
            {sections.filter(s => !s.visible).map(section => (
              <Button key={section.id} variant="outline" size="sm" onClick={() => toggleVisibility(section.id)}>
                <Eye className="h-4 w-4 mr-2" />
                {section.name}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
